import { type Candidate } from "@vidario/domain";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { useVidarioClient } from "../client";
import { queryKeys } from "../queryKeys";

export interface UploadCandidateAttachmentInput {
  /** URL-derived jobId — used for cache invalidation. */
  jobId: string;
  candidateId: string;
  kind: "cv" | "photo";
  file: File;
}

/** Multipart upload of a CV or photo for a candidate. Backend stores
 * the file in the S3-compatible bucket and returns the candidate with
 * the resulting `cvUrl` / `photoUrl` populated. Updates the active
 * candidate cache + invalidates the per-job candidates list so the
 * Longlist + Recommendation views refetch. */
export function useUploadCandidateAttachment(): UseMutationResult<
  Candidate,
  Error,
  UploadCandidateAttachmentInput
> {
  const client = useVidarioClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, kind, file }) =>
      client.uploadCandidateAttachment({ candidateId, kind, file }),
    onSuccess: (candidate, { jobId, candidateId }) => {
      qc.setQueryData(queryKeys.candidate(jobId, candidateId), candidate);
      qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
      // Refetch the activity-log so the new "Anhang hochgeladen" entry
      // appears in the ContactSidebar without a full page reload. Key
      // shape matches useCandidateLog: ["candidates", id, "log"].
      qc.invalidateQueries({ queryKey: ["candidates", candidateId, "log"] });
    },
  });
}
